import { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'wouter';

import { SessionContext, useSession } from '../../session';
import { ApiClientError } from '../../api/http';
import { Project, createProject, getProject, listProjects } from '../../api/projects';
import { CONTENT_CLASSES, listStorageLocations, putStoragePreferences } from '../../api/storage';
import type { ContentClass, StorageLocation } from '../../api/storage';
import { WorkspacesPanel } from './WorkspacesPanel';
import { ResumeSetupBanner } from '../Onboarding/ResumeSetupBanner';

/** The one line that explains where projects live (walkthrough F-55, F-59, F-61, F-72). */
export const PROJECTS_LIVE_IN_STUDIO = 'Your projects live inside your studio. Open it, then create your first project there.';

const STORAGE_KIND_LABELS: Record<StorageLocation['kind'], string> = {
  cloud_object: 'Cloud storage',
  node_local: 'Local Node storage',
  node_nas: 'Local Node storage',
};

export function ProjectCollection({ projects, error }: { projects: Project[] | null; error: string }) {
  if (error) return <p role="alert" className="error">{error}</p>;
  if (projects === null) return <p role="status">Loading projects…</p>;
  if (projects.length === 0) return <p className="empty-state">No projects yet. Create the first one below.</p>;
  return (
    <div className="card-grid" aria-label="Project list">
      {projects.map((project) => (
        <article className="project-card" key={project.project_id}>
          <span className="badge">{project.status}</span>
          <h2>{project.display_name}</h2>
          <p><code>{project.slug}</code></p>
          <Link to={`/projects/${encodeURIComponent(project.project_id)}`}>Open project</Link>
        </article>
      ))}
    </div>
  );
}

/**
 * The signed-in home. A customer sees one path: open the studio, and create projects inside it. The
 * control-plane project list and its create form are for the deployment's owner organization only.
 */
export function ProjectsPage() {
  const { session } = useSession();
  return session?.owner_org === true ? <OwnerProjectsPage /> : <CustomerHomePage />;
}

export function CustomerHomePage() {
  return (
    <section className="projects-page customer-home">
      <header className="page-heading">
        <p className="eyebrow">Home</p>
        <h1>Your studio</h1>
        <p>{PROJECTS_LIVE_IN_STUDIO}</p>
      </header>
      <ResumeSetupBanner />
      <ol className="home-steps">
        <li>
          <h2>1. Your studio</h2>
          <p className="settings-muted">Your private place to write, plan and create. Open it to get started.</p>
          <WorkspacesPanel heading={null} />
        </li>
        <li>
          <h2>2. Create your first project</h2>
          <p className="settings-muted">
            Inside the studio, choose Create Project. You can also work from your own AI tool:{' '}
            <Link to="/connect">connect it to your studio</Link>.
          </p>
        </li>
      </ol>
    </section>
  );
}

function OwnerProjectsPage() {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState('');
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [storageLocations, setStorageLocations] = useState<StorageLocation[] | null>(null);
  const [storageOverride, setStorageOverride] = useState('');
  const [storageNotice, setStorageNotice] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      setProjects(await listProjects());
    } catch (failure) {
      setProjects(null);
      setError(failure instanceof Error ? failure.message : 'projects could not be loaded');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let active = true;
    listStorageLocations()
      .then((locations) => active && setStorageLocations(locations))
      .catch(() => {
        if (active) {
          setStorageLocations([]);
          setStorageNotice('Storage choices could not be loaded. This project will use organization defaults.');
        }
      });
    return () => { active = false; };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const project = await createProject({ slug, displayName });
      setProjects((current) => [...(current || []), project]);
      setSlug('');
      setDisplayName('');
      if (storageOverride) {
        const values = CONTENT_CLASSES.reduce((current, contentClass) => ({
          ...current,
          [contentClass]: storageOverride,
        }), {} as Partial<Record<ContentClass, string | null>>);
        try {
          await putStoragePreferences({ projectId: project.project_id, values });
          setNotice('Project created with its storage choice. This affects new files only.');
        } catch {
          setNotice('Project created, but its storage choice could not be saved. You can set it in Settings.');
        }
      } else {
        setNotice('Project created. It will use your organization storage defaults.');
      }
      setStorageOverride('');
    } catch (failure) {
      setError(
        failure instanceof ApiClientError || failure instanceof Error
          ? failure.message
          : 'the project could not be created',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="projects-page">
      <header className="page-heading">
        <p className="eyebrow">Workspace</p>
        <h1>Projects</h1>
        <p>Each book, film or series you work on is a project. Your projects live inside your workspace, your private studio.</p>
      </header>
      <ResumeSetupBanner />
      <ProjectCollection projects={projects} error={error} />
      <WorkspacesPanel />
      <form className="inline-form" onSubmit={submit}>
        <h2>Create a project</h2>
        <label htmlFor="project-name">Display name</label>
        <input id="project-name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required />
        <label htmlFor="project-slug">Short name (lowercase letters, numbers and dashes)</label>
        <input id="project-slug" value={slug} pattern="[a-z0-9][a-z0-9-]*" onChange={(event) => setSlug(event.target.value)} required />
        <label htmlFor="project-storage-override">Where should this project&apos;s files live?</label>
        <select
          id="project-storage-override"
          value={storageOverride}
          disabled={storageLocations === null}
          onChange={(event) => setStorageOverride(event.target.value)}
        >
          <option value="">Use organization defaults</option>
          {(storageLocations || []).map((location) => (
            <option
              key={location.location_id}
              value={location.location_id}
              disabled={location.availability.state !== 'available'}
            >
              {location.label} — {STORAGE_KIND_LABELS[location.kind]}
              {location.availability.state !== 'available' ? ' (Unavailable)' : ''}
            </option>
          ))}
        </select>
        {storageLocations === null ? <p role="status">Loading storage choices…</p> : null}
        <p className="settings-muted">
          Choosing a location applies it to all five file types for this project. Existing files will not move.
        </p>
        {storageNotice ? <p className="settings-muted" role="status">{storageNotice}</p> : null}
        {notice ? <p className="success" role="status">{notice}</p> : null}
        {!busy && (!slug || !displayName) ? (
          <p className="settings-muted" id="create-project-why">Enter a display name and a short name to create the project.</p>
        ) : null}
        <button disabled={busy || !slug || !displayName} aria-describedby={!slug || !displayName ? 'create-project-why' : undefined}>
          {busy ? 'Creating…' : 'Create project'}
        </button>
      </form>
    </section>
  );
}

/**
 * The project's navigation cards. Links are absolute: a relative ``to="tasks"``
 * resolved against ``/projects/<id>`` to ``/projects/tasks``, which the router
 * then read as a project named "tasks" (found by the P9 staging test).
 */
export function ProjectNavCards({ projectId, ownerOrg }: { projectId: string; ownerOrg: boolean }) {
  const base = `/projects/${encodeURIComponent(projectId)}`;
  return (
    <div className="card-grid">
      {/* Brain, memory and hosted tasks serve only the owner org (DESIGN.md §18). */}
      {ownerOrg ? (
        <>
          <Link className="nav-card" data-area="brain" to={`${base}/brain`}>Project Brain</Link>
          <Link className="nav-card" data-area="memory" to={`${base}/memory`}>Context &amp; memory</Link>
          <Link className="nav-card" data-area="tasks" to={`${base}/tasks`}>Tasks</Link>
        </>
      ) : null}
      <Link className="nav-card" data-area="runs" to={`/runs?project=${encodeURIComponent(projectId)}`}>Runs</Link>
    </div>
  );
}

export function ProjectOverviewPage({ projectId }: { projectId: string }) {
  const ownerOrg = useContext(SessionContext)?.session?.owner_org === true;
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    getProject(projectId).then((value) => active && setProject(value)).catch((failure) => active && setError(failure instanceof Error ? failure.message : 'project could not be loaded'));
    return () => { active = false; };
  }, [projectId]);
  if (error) return <p role="alert" className="error">{error}</p>;
  if (!project) return <p role="status">Loading project…</p>;
  return (
    <section>
      <p className="eyebrow">Project</p>
      <h1>{project.display_name}</h1>
      <p><span className="badge">{project.status}</span> <code>{project.project_id}</code></p>
      <ProjectNavCards projectId={projectId} ownerOrg={ownerOrg} />
    </section>
  );
}
